import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "codebench-theme";

/**
 * The user's own choice, or null while they are still following the system.
 * A lab machine is often set to light and a student's laptop to dark, so the
 * system preference is the default and an explicit pick sticks per browser.
 */
function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    // Private mode, or storage disabled by policy: fall back to the system.
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

/**
 * The stylesheet only ever reads data-theme, so the attribute has to be set
 * before the first paint. index.html does that inline; this keeps it in step.
 */
function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({
  theme: "dark",
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [explicit, setExplicit] = useState<Theme | null>(storedTheme);
  const [system, setSystem] = useState<Theme>(systemTheme);
  const theme = explicit ?? system;

  // Only matters while the user has made no choice, but the listener is cheap
  // and keeping it attached avoids a stale reading if they clear the setting.
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? "light" : "dark");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setExplicit((current) => {
      const next: Theme = (current ?? systemTheme()) === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // A theme that lasts only for this tab is better than a failed click.
      }
      return next;
    });
  }, []);

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/** The Monaco theme that matches the page. */
export function useEditorTheme() {
  return useTheme().theme === "light" ? "vs" : "vs-dark";
}

/** The one control that switches the theme, wherever a page has room for it. */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      className="theme-toggle"
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === "dark" ? "☾" : "☀"}
    </button>
  );
}
